import { useAtomValue } from 'jotai'
import { useCartLocalPricing } from '@/core/cart/useCartPricing'
import { usePriceMode } from '@/core/cart/useCart'
import { hotFormatRawAtom } from '@/core/store/hotFormat.store'
import { omanFormatDataAtom, omanCartSyncingAtom, omanSelectedShippingMethodAtom } from '../stores/oman.store'
import { v2CartAtom } from '@/core/cart/cart.store'

// Parse a `{ value: string }` shape coming from `hotFormatRaw` into a
// number. Returns NaN when missing so callers can chain with `||` to fall
// back to other sources without short-circuiting on a real `0`.
const fromFormatTotal = (v?: { value?: string }) => parseFloat(v?.value ?? '')

/**
 * Order pricing for Oman's pre-summary.
 *
 * Each field has up to three potential sources, picked in this order:
 *
 *   1. **`hotFormatRaw.ushop.u23.formatTotal`** — freshest server result
 *      after a successful `GET hot/format`. Preferred when present.
 *   2. **`omanFormatDataAtom`**                — mapped form of the same
 *      response. Used as the second source for fields not exposed by
 *      `formatTotal` (e.g. product credit).
 *   3. **Local FE math** (`useCartLocalPricing`) — instant fallback so the
 *      summary still shows numbers during the 1s sync debounce or before
 *      the first server response arrives.
 *
 * `0` is a meaningful value (free shipping) so we use `NaN || x` chaining
 * via `fromFormatTotal` rather than nullish coalescing.
 *
 * **Optimistic mode (`isCartSyncing`)**: while the user is mid-click and
 * the debounced sync hasn't fired yet, the server values are stale by
 * definition. We bypass them and prefer LOCAL math for `subtotal`/`total`
 * so the numbers update in lock-step with the +/- buttons. `freight` and
 * `productCredit` have no purely-local equivalent, so we keep the last
 * known server values as a best-effort estimate during the window.
 */
export const useOmanOrderPricing = () => {
    const priceMode = usePriceMode()
    const omanV2Cart = useAtomValue(v2CartAtom)
    const local = useCartLocalPricing(priceMode, omanV2Cart)
    const formatData = useAtomValue(omanFormatDataAtom)
    const hotFormatRaw = useAtomValue(hotFormatRawAtom)
    const isCartSyncing = useAtomValue(omanCartSyncingAtom)
    const selectedShippingMethod = useAtomValue(omanSelectedShippingMethodAtom)

    const formatTotal = (hotFormatRaw as { ushop?: { u23?: { formatTotal?: Record<string, { value?: string } | undefined> } } } | null | undefined)?.ushop?.u23
        ?.formatTotal

    const hasFormatItems = !!formatData?.items?.length

    // Local subtotal: pure cart math when we have one, otherwise sum the
    // server-provided line items so the summary still matches what's on
    // screen for read-only / freebie-only states.
    const localSubtotal =
        local.hasV1Cart || local.hasV2Cart ? local.subtotal : hasFormatItems ? formatData!.items!.reduce((sum, it) => sum + it.price * it.qty, 0) : 0

    const productCredit = formatData?.product_credit ?? 0

    // While syncing, ignore server subtotal/total (they're stale until
    // GET returns). Freight/credit have no local source — keep last
    // known values as an estimate.
    const subtotal = isCartSyncing ? localSubtotal : fromFormatTotal(formatTotal?.subtotal) || formatData?.subtotal || localSubtotal
    const serverFreight = isCartSyncing ? (formatData?.freight ?? 0) : fromFormatTotal(formatTotal?.delivery_fee) || (formatData?.freight ?? 0)
    const freight = selectedShippingMethod === 'pickup' ? 0 : serverFreight
    const total = isCartSyncing
        ? subtotal + freight - productCredit
        : fromFormatTotal(formatTotal?.total_price) || formatData?.total || subtotal + freight - productCredit

    return {
        v1Entries: local.v1Entries,
        v2CartEntries: local.v2CartEntries,
        isV2: local.isV2,
        hasV1Cart: local.hasV1Cart,
        hasV2Cart: local.hasV2Cart,
        hasFormatItems,
        subtotal,
        freight,
        productCredit,
        total,
        // Expose split sources for downstream uses (debug, alt UI, tests).
        localSubtotal,
        serverSubtotal: formatData?.subtotal
    }
}
